import { lazy, ComponentType } from "react";

type ComponentModule = { default: ComponentType<Record<string, unknown>> };

/**
 * Wraps React.lazy() with automatic retry logic for chunk-loading failures.
 * Retries up to `maxRetries` times with exponential backoff before giving up.
 * On final failure the error propagates to the nearest ErrorBoundary.
 */
export function lazyWithRetry(
  factory: () => Promise<ComponentModule>,
  maxRetries = 3,
): React.LazyExoticComponent<ComponentType<Record<string, unknown>>> {
  return lazy(() => retryImport(factory, maxRetries));
}

async function retryImport(
  factory: () => Promise<ComponentModule>,
  maxRetries: number,
): Promise<ComponentModule> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;

      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        // Exponential backoff: 500ms, 1000ms, 2000ms …
        const delay = Math.pow(2, attempt) * 500;
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Bust the module cache so the browser re-fetches the chunk
        // (only works for import URLs that haven't been permanently cached)
        console.warn(
          `[lazyWithRetry] Chunk load failed (attempt ${attempt + 1}/${maxRetries}), retrying in ${delay}ms…`,
        );
      }
    }
  }

  throw lastError;
}
